import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { TrackeritemTimelineHelper } from './trackeritem.timeline.helper';
import { NotificationService } from '../../../../core/services/core.notification.service';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { SettingService } from '../../../../core/services/core.setting.service';
import { TrackeritemService } from '../../services/trackeritem.service';
import { UiService } from '../../../../core/services/core.ui.service';
import { ProjectModel } from '../../../../core/services/project.model.service';
import { UploadService } from '../../../../core/services/core.upload.service';
import { UserService } from '../../../../core/services/core.user.service';
import { TrackeritemRest } from '../../services/trackeritem.rest';
import { MentionsService, MentionUser, MentionConfig } from '../../../../core/services/core.mentio.service';
import { trackerMentionUsers } from '../../services/trackeritem.mentions';
import { StateService } from '@uirouter/angular';
import { GfMarkdownEditorComponent } from '../../../../widgets/gf-markdown-editor/gf-markdown-editor.component';
import { FilePreviewService } from '../../../../widgets/gf-file-preview/gf-file-preview.service';
import { marker } from '../../../../core/services/ngx-translate-extract-marker';

@Component({
  selector: 'trackeritem-edit-timeline',
  template: require('./trackeritem.timeline.component.html')
})
export class TrackeritemEditTimelineComponent implements OnInit, OnDestroy {
  @ViewChild('followupEditor', { static: false }) followupEditor!: GfMarkdownEditorComponent;
	@Input() project: any;
	@Input() tracker: any;
	@Input() trackeritem: any;
	@Input() acls: any;
	@Input() followupRefId: any;
	@Input() printMode: any;

  @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();
	@Output() onFollowupDelete: EventEmitter<any> = new EventEmitter<any>();
	@Output() onFileSelected: EventEmitter<any> = new EventEmitter<any>();

	constructor(
		private notificationService: NotificationService,
		private translateService: GfTranslateService,
		private settingService: SettingService,
		private trackeritemService: TrackeritemService,
		private trackeritemTimelineHelper: TrackeritemTimelineHelper,
		private httpClient: HttpClient,
		private uiService: UiService,
		private projectModel: ProjectModel,
		private uploadService: UploadService,
		private userService: UserService,
		private trackeritemRest: TrackeritemRest,
		private mentioService: MentionsService,
    private stateService: StateService,
    public filePreviewService: FilePreviewService,
	) {}

  idMarkedForDelete: number = 0;
  scmModule: boolean = false;
  hasSessionUser: boolean = false;
  maxUploadSize: number = 0;
  timelineItems: any[] = [];
  editingFollowupId: number = 0;
  expandedCalloutIds: { [trackById: string]: boolean } = {};
  timelineSettings: any = {};
  overwriteError: boolean = false;
  editingFollowupText: string = '';
  followup: string = '';
  
  // The @-mention source shared by the follow-up and edit editors.
  mentions: MentionConfig | null = null;
  currentUser: any;

  ngOnInit(): void {
    this.printMode = (typeof this.printMode === 'boolean') ? this.printMode : false;
    this.scmModule = !_.isEmpty(this.projectModel.getEnabledScm(this.project));
    this.hasSessionUser = this.userService.session.hasUser();
    this.currentUser = this.userService.session.getUser();
    this.followup = this.trackeritem.followup;
    this.maxUploadSize = this.uploadService.getMaxUploadSize('tracker');

    // When the tracker item is saved, clear the in-memory followup draft
    this.notificationService.subscribe('ti-edit-timeline', 'TRACKER_ITEM.EDIT.UPDATED', (params) => {
      const p = params || {};
      if (!p.preventCleanup) {
        this.trackeritemService.clearFollowupDraft(this.tracker.id, this.trackeritem.id);
      }
      this.updateTimeline(p);
    });

    this.getTimelineSettings();
    this.updateTimeline({ preventCleanup: true });

    // Restore any unsaved followup draft from a previous visit to this item
    const savedDraft = this.trackeritemService.getFollowupDraft(this.tracker.id, this.trackeritem.id);
    if (savedDraft) {
      this.followup = savedDraft;
    }

    this.initializeMentionConfigs(); // Initialize mention configuration
    this.editingFollowupId = 0;
  }

  ngOnDestroy(): void {
    this.notificationService.unsubscribe('ti-edit-timeline');

    this.mentions?.destroy();
  }

  updateTimeline(params: any = {}): void {
    this.timelineItems = this.trackeritemTimelineHelper.createTimeline(
      _.get(params, 'trackeritem', this.trackeritem),
      _.get(params, 'tracker', this.tracker)
    );
    
    let attachment = _.find(this.timelineSettings, {key: 'attachment'});
    if (attachment) {
      attachment.count = _.get(this.trackeritem, 'rel.attachments', []).length;
    }

    let commit = _.find(this.timelineSettings, {key: 'commit'});
    if (commit) {
      commit.count = _.filter(this.timelineItems, { activityOrigin: 'commit' }).length;
    }

    let event = _.find(this.timelineSettings, {key: 'event'});
    if (event) {
      event.count = _.filter(this.timelineItems, (i) => ['build', 'approval', 'deployment'].includes(i.activityOrigin)).length;
    }
   
    if (params && !params.preventCleanup) {
      // Clears the followup
      this.followup = '';
      this.trackeritemService.clearFollowupDraft(this.tracker.id, this.trackeritem.id);

      // Clear editor content in-place to avoid unmount/remount visual blinking.
      if (this.followupEditor) {
        this.followupEditor.setMarkdown('');
      }
    }
  }

  unmarkForDelete(): void {
    this.idMarkedForDelete = 0;
  }

  filterTimelineSettings(item: any): boolean {
    if (!this.scmModule && (item.type == 'commit' || item.type == 'merge_comment')) {
      return false;
    }
    return true;
  }

  canEditDelete(item: any): boolean {
    return (
      item.activityOrigin == 'followup' &&
      this.trackeritem.lockStatus == 0 &&
      (
        this.acls.isAdmin ||
        this.acls.canUpdate && item.user.id == _.get(this.userService.getUser(), 'id', 0)
      )
    );
  }

  canDeleteAttachment(item: any): boolean {
    if (!item?.attachment?.attachmentUrl) return false;
    if (_.get(this.trackeritem, 'lockStatus', 0) > 0) return false;
    if (this.acls.isAdmin) return true;
    if (!this.acls.canUpdate) return false;
    if (item.user.id == _.get(this.userService.getUser(), 'id', 0)) return true;
    return false;
  }

  cancelFollowupEdit(ev: any, item: any): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.editingFollowupId = 0;
    this.idMarkedForDelete = 0;
  }

  enableFollowupEdit(item: any = {}): void {
    if (this.trackeritem.currentLockStatus > 0) return;
    if (!(item.activityOrigin == 'followup')) return;
    if (this.editingFollowupId == item.id) return;
    this.editingFollowupId = item.id;
    this.editingFollowupText = item.body;
  }

  /**
   * Section for image uploads from the new follow-up composer. The message does
   * not exist yet, so uploads go into the temporary section keyed by
   * `followupRefId`; the backend re-points them at the real message once the
   * follow-up is saved (see TrackerItemService::sendFollowup).
   */
  get followupUploadSection(): string {
    return this.trackeritemService.getFollowupSectionTmp();
  }

  /** Section for image uploads when editing an already saved follow-up. */
  get editFollowupUploadSection(): string {
    return this.trackeritemService.getFollowupSection();
  }

  onFollowpEdited(payload: { refId: number, content: string }) {
    this.editingFollowupText = payload.content;
  }

  saveFollowup(item: any): void {
    var saveItem = {
      id: item.id,
      body: this.editingFollowupText,
      bodyFormat: 'text/markdown'
    };

    this.followupEdit(saveItem, this.trackeritem.id)
      .then((response: any) => {
        item.body = (response && response.body !== undefined) ? response.body : this.editingFollowupText;
        this.editingFollowupText = item.body;
        this.editingFollowupId = 0;
      });
  }

  deleteFollowup(ev: any, item: any): void {
    ev.preventDefault();
    ev.stopPropagation();

    if (item.id == this.idMarkedForDelete) {
      this.trackeritemService.followupDelete(item.id, this.trackeritem.id)
        .then(() => {
          // Rebuild the local timeline items list,
          // After asking the helper to remove the deleted item
          this.timelineItems = this.trackeritemTimelineHelper.createTimeline(
            this.trackeritemTimelineHelper.removeFollowup(this.trackeritem, item), 
            this.tracker
          );
          this.onFollowupDelete.emit();
        }, () => {});
    } else {
      this.idMarkedForDelete = item.id;
    }
  }

  onFollowup(body: string) {
    // Persist the draft in memory so it survives tab switches within the item
    this.trackeritemService.setFollowupDraft(this.tracker.id, this.trackeritem.id, body);
    this.onUpdate.emit({followup: body, followupFormat: 'text/markdown'});
  }

  timelineTypeActive(activityType: string): boolean {
    if (activityType == 'activity_log') {
      activityType = 'audit_trail';
    }
    if (['build', 'approval', 'deployment'].includes(activityType)) {
      activityType = 'event';
    }

    var item = _.find(this.timelineSettings, {key: activityType});
    return !_.isEmpty(item) ? item.active : false;
  }

  isMarkedForDelete(id: number): boolean {
    return id == this.idMarkedForDelete;
  }

  timelineClass(item: any): any {
    const changeOrigins = ['followup', 'merge_comment', 'activity_log'];
    const changeTypes = ['update', 'notebook_page_Updated', 'notebook_page_Restored'];
    const createTypes = ['create', 'attachment_Added', 'notebook_page_Added', 'notebook_attachment_Added'];
    const isFollowUp = (item.activityOrigin == 'followup');
    const isCommit = (item.activityOrigin == 'commit');
    const isEvent = ['build', 'approval', 'deployment'].includes(item.activityOrigin);
    const closeTypes = ['close', 'attachment_Removed', 'notebook_page_Deleted', 'notebook_attachment_Removed'];

    return {
      'update': changeOrigins.includes(item.activityOrigin) || changeTypes.includes(item.activityType) || (isEvent && !item.succeeded),
      'create': isCommit || (isEvent && item.succeeded) || ((!isFollowUp) && createTypes.includes(item.activityType)),
      'delete': (!isFollowUp) && closeTypes.includes(item.activityType),
      'editing': this.editingFollowupId == item.id
    };
  }

  timelineGlyph(item: any): string {
    const isCommit = (item.activityOrigin == 'commit');
    const isFollowUp = ['followup', 'merge_comment'].includes(item.activityOrigin);
    const isAttachment = ['attachment_Added', 'attachment_Removed'].includes(item.activityType);
    const isEvent = ['build', 'approval', 'deployment'].includes(item.activityOrigin);

    if (isEvent) return 'build';
    if (isCommit) return 'scmcommit';
    if (isFollowUp) return 'discussion';
    if (isAttachment) return 'docmanfileversion';
    return 'trackeritem';
  }

  timelineTypeButtonClass(item: any): any {
    const on = this.timelineTypeActive(item.type);
    return {
      'btn-primary': on,
      'btn-trans': !on
    };
  }

  toggleTimeline(item: any): void {
    const on = this.timelineTypeActive(item.type);
    item.active = (on == false);
    this.saveTimelineSettings();
  }

  toggleCalloutDetail(ev: any, item: any): void {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    this.expandedCalloutIds[item.trackById] = !this.expandedCalloutIds[item.trackById];
  }

  isCalloutDetailExpanded(item: any): boolean {
    return !!this.expandedCalloutIds[item.trackById];
  }

  deleteAttachment(ev: any, element: any, requireConfirm: boolean = true): Promise<void> {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    if (element.id == this.idMarkedForDelete || !requireConfirm) {
      const attachment = element.attachment;

      if (!attachment) {
        return Promise.resolve().then(() => {
        });
      }
      const attachmentId = attachment.id;

      return this.trackeritemRest.deleteAttachment(this.trackeritem.id, attachmentId)
        .then((response: any) => {
          _.remove(this.trackeritem.rel.attachments, { id: attachment.id });
          this.trackeritem.rel.history.unshift(this.createTempHistoryForDeleteAttachment(attachment));

          if (_.has(response, 'lastModifiedDate')) {
            this.trackeritem.lastModifiedDate = response.lastModifiedDate;
          }

          this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', { trackeritem: this.trackeritem, preventCleanup: true });
        },(err) => {
          if (_.has(err, 'data.overwrite')) {
            this.overwriteError = true;
          }
        })
        .then(() => {
          this.idMarkedForDelete = 0;
        });

    } else {
      return Promise.resolve().then(() => {
        this.idMarkedForDelete = element.id;
      });
    }
  }

  timelineSettingLabel(item: any): string {
    if (['attachment', 'commit', 'event'].includes(item.type)) {
      return item.label + ' (' + _.get(item, 'count', 0) + ')';
    }
    return item.label;
  }

  createTempHistoryForDeleteAttachment(attachment: any): any {
    return {
      "activityOrigin": "audit_trail",
      "activityDate": new Date().toISOString(),
      "user": attachment.postedBy,
      "data": [
        {
          "fieldname": "attachment_Removed",
          "oldvalue": attachment.fileName,
          "newvalue": "None"
        }
      ],
      "activityType": "update"
    };
  }

  getTimelineSettings(): void {
    const defaults = this.getTimelineSettingsDefaults();
    const settings = _.get(this.tracker, 'rel.settings.timeline', {});

    this.timelineSettings = _.keys(defaults).reduce((carry, key) => {
      let obj = {
        key: key,
        ...defaults[key],
        active: _.get(settings, `${key}.active`, defaults[key].active)
      };
      carry.push(obj);
      return carry;
    }, []);

    if (!this.scmModule) {
      _.set(this.tracker, 'rel.settings.timeline.commit.active', false);
      _.set(this.tracker, 'rel.settings.timeline.merge_comment.active', false);
    }
  }

  saveTimelineSettings(): void {
    const timelineSettings = _.reduce(
      this.timelineSettings,
      (carry, item) => {
        carry[item.type] = {
          active: item.active
        };
        return carry;
      },
      {}
    );

    const params = {
      settingName: 'timeline',
      section: 'tracker',
      refId: this.tracker.id,
      settingValue: timelineSettings
    };

    this.settingService.post(params)
      .then(() => {}, () => {});
  }

  refIdGetter(): number {
    return this.editingFollowupId;
  }

  followupEdit(followup: any, trackerItemId: number): Promise<any>  {
    return this.httpClient.post(`api/trackeritem/${trackerItemId}/followup/${followup.id}`, followup)
	.toPromise()
	.then((response: any) => {
      this.uiService.message.showSuccess("Follow up saved");
      return response;
    }, () => {
      this.uiService.message.showError("There was an error trying to save the follow up");
    });
  }

  followupDelete(followup: any, trackerItemId: number): Promise<void>  {
    return this.httpClient.delete(`api/trackeritem/${trackerItemId}/followup/${followup.id}`)
	.toPromise()
	.then(() => {
      this.uiService.message.showSuccess("Follow deleted");
    }, () => {
      this.uiService.message.showError("There was an error trying to delete the follow up");
    });
  }

  timelineItem(type: string, label: string, defaultOn: boolean): any {
    return {
      type: type,
      label: this.translateService.translate(label),
      active: defaultOn
    };
  }

  getTimelineSettingsDefaults(): any {
    const hasScm = !_.isEmpty(this.scmModule);
    return {
      followup: this.timelineItem('followup', marker('Follow ups'), true),
      attachment: this.timelineItem('attachment', marker('Attachments'), true),
      audit_trail: this.timelineItem('audit_trail', marker('Changes'), true),
      commit: this.timelineItem('commit', marker('Commits'), hasScm),
      merge_comment: this.timelineItem('merge_comment', marker('Merge Comments'), hasScm),
      event: this.timelineItem('event', marker('Events'), true)
    };
  }

  fileSelected(event: any) {
    this.onFileSelected.emit(event);
  }

  initializeMentionConfigs(): void {
    this.mentions = this.mentioService.createMentionConfig({
      project: () => this.project?.unixName || '',
      seed: () => this.getTrackerUsers(),
      roles: () => this.project?.rel?.roles || []
    });
  }

  /**
   * Get all users related to the tracker item.
   *
   * Follow-up authors are this surface's own contribution -- they live on the
   * rendered timeline rather than on the tracker item.
   */
  getTrackerUsers(): MentionUser[] {
    return trackerMentionUsers(
      this.mentioService,
      this.tracker,
      this.trackeritem,
      (this.timelineItems || []).map(item => item.user)
    );
  }

  trackById(index: number, item: any): any {
    return item.trackById;
  }
}
